// Contenido de la guía /configuracion-eventos/flujo. Cada nodo describe un
// tramo del asistente de alta; las reglas reflejan las validaciones de
// utils.validarPaso y de ConfiguracionEventoService (API).
//
// tipo: "inicio" | "paso" | "decision" | "fin"
// detalle: { campos, restricciones, aTenerEnCuenta, ejemplos } (listas de texto)

export const SECCIONES_DETALLE = [
  { id: "campos", titulo: "Qué se completa", icono: "edit_note", color: "uproBlue" },
  { id: "restricciones", titulo: "Restricciones", icono: "block", color: "uproPink" },
  { id: "aTenerEnCuenta", titulo: "A tener en cuenta", icono: "lightbulb", color: "uproYellow" },
  { id: "ejemplos", titulo: "Ejemplos", icono: "format_quote", color: "uproGreen" },
];

export const FLUJO_EVENTO = [
  {
    id: "inicio",
    tipo: "inicio",
    icono: "play_arrow",
    titulo: "Antes de empezar",
    resumen: "En «Configuración de eventos» presione «Nuevo evento».",
    detalle: {
      campos: [
        "El asistente tiene 7 pasos que se recorren en orden. Al crear, «Siguiente» solo avanza si el paso actual es válido.",
        "Al editar un evento existente se puede saltar a cualquier paso desde la lista lateral.",
      ],
      aTenerEnCuenta: [
        "Los productos a vender tienen que existir previamente en Productos (los ingredientes no se pueden vender).",
        "Si va a vender menú universitario, cargue antes los platos de esos días en «Menú universitario».",
        "Los vouchers físicos se administran en «Beneficios y vouchers»; el asistente solo los controla.",
        "El punto de venta de eventos opera únicamente en la sede Villa Mercedes y en horario argentino.",
        "Si sale con «Cancelar» y hay cambios sin guardar, se pide confirmación. Nada se guarda hasta el último paso.",
      ],
      ejemplos: [
        "Para repetir un evento ya configurado use «Duplicar» en el menú ⋮ del listado: copia todo menos el horario y agrega «(copia)» al nombre.",
      ],
    },
  },
  {
    id: "datos",
    tipo: "paso",
    numero: 1,
    icono: "event",
    titulo: "Datos del evento",
    resumen: "Nombre y horario en que funciona.",
    etiqueta: "Obligatorio",
    detalle: {
      campos: [
        "Nombre del evento (obligatorio).",
        "Inicio y Fin: fecha y hora (obligatorios). Debajo del fin se muestra la duración calculada.",
        "Evento activo: por defecto queda activo. Un evento desactivado no habilita el punto de venta ni bloquea la app.",
      ],
      restricciones: [
        "El nombre no puede quedar vacío y admite hasta 255 caracteres.",
        "El fin tiene que ser posterior al inicio y el evento puede durar hasta 31 días. Si ya comenzó, el cierre puede llegar hasta 31 días desde ahora (o mantener uno guardado más lejano); al extender el cierre de un evento finalizado no hay límite.",
        "El fin tiene que ser futuro: no se crean eventos que ya terminaron.",
        "Si el evento ya comenzó o tiene ventas, el inicio queda fijo y el fin no puede quedar antes de ahora ni de la última venta.",
        "No puede haber dos eventos activos con horarios superpuestos (se controla en el paso 7 y al guardar).",
      ],
      aTenerEnCuenta: [
        "El evento está vigente desde el inicio hasta antes del fin: dos eventos pueden encadenarse en el mismo minuto.",
        "Si el horario cruza la medianoche, el punto de venta y los bloqueos también quedan activos de noche. Para limitarlos a cada jornada, cree un evento por día.",
        "Un evento desactivado no cuenta para la superposición.",
        "Al editar, los cambios se aplican a las ventas siguientes; las ya registradas conservan sus precios.",
      ],
      ejemplos: [
        "«CONAFO 2026» · Inicio 23/09/2026 08:00 · Fin 23/09/2026 18:00 → Duración: 10 h.",
        "Evento A termina 14:00 y evento B empieza 14:00 → válido, no se superponen.",
        "Congreso de 3 días de 8 a 18 h → cree el primer día y duplíquelo para los otros dos.",
      ],
    },
  },
  {
    id: "productos",
    tipo: "paso",
    numero: 2,
    icono: "inventory_2",
    titulo: "Productos a la venta",
    resumen: "Qué se vende y a qué precio.",
    etiqueta: "Opcional si vende menú",
    detalle: {
      campos: [
        "«Agregar productos» abre un buscador por nombre y categoría; se pueden marcar varios y agregarlos juntos.",
        "Precio en el evento de cada producto (opcional).",
        "Interruptor «A la venta» / «Pausado».",
        "Flechas para ordenar y botón para quitar el producto del evento.",
      ],
      restricciones: [
        "No se muestran ni se aceptan ingredientes.",
        "Un producto no puede estar repetido.",
        "El precio tiene que ser mayor que cero, hasta $ 9.999.999,99 y con dos decimales como máximo. Si queda vacío se cobra el precio de carta.",
        "Un producto a la venta sin precio de evento necesita precio de carta mayor que cero; si no lo tiene, póngale precio o páuselo.",
        "Un producto que ya tiene ventas en el evento no se puede quitar: páuselo.",
      ],
      aTenerEnCuenta: [
        "El orden de la lista es el orden en que el vendedor ve los productos en el punto de venta.",
        "Un producto pausado sigue en el evento pero no se vende; sirve para retomarlo sin volver a cargarlo.",
        "Si un producto vendido se da de baja o pasa a ingrediente, queda en el evento solo pausado.",
        "El punto de venta necesita algo para vender: al menos un producto a la venta o el menú universitario (paso 3).",
      ],
      ejemplos: [
        "Café con leche · carta $2.500 · precio vacío → en el evento se cobra $2.500.",
        "Agua mineral · carta $1.500 · precio $1.800 → se cobra $1.800 solo durante el evento.",
        "Precio $0 → no se acepta: un producto sin cargo no se vende desde el punto de venta.",
      ],
    },
  },
  {
    id: "menu",
    tipo: "paso",
    numero: 3,
    icono: "restaurant_menu",
    titulo: "Menú universitario",
    resumen: "Platos del cronograma del día.",
    etiqueta: "Opcional",
    detalle: {
      campos: [
        "Interruptor «Vender platos del menú universitario».",
        "Precio de cada plato: «Tarifa de cada comprador» o «Precio fijo».",
        "Check para incluir o excluir el menú del descuento de Facturación en ambas modalidades.",
      ],
      restricciones: [
        "Con precio fijo, el importe tiene que ser mayor que cero, hasta $ 9.999.999,99 y con dos decimales como máximo.",
        "Para que aparezcan platos tiene que existir la categoría «Menú universitario», haber turnos activos en Villa Mercedes y platos cargados en el cronograma (se avisa en el paso 7).",
      ],
      aTenerEnCuenta: [
        "Se ofrecen los platos del cronograma del día para el turno más cercano a la hora actual.",
        "Tarifa de cada comprador: se calcula por el DNI ingresado; sin DNI o fuera de la comunidad UPrO se cobra la tarifa Particular.",
        "Precio fijo: el mismo importe para todos los platos y todos los compradores.",
      ],
      ejemplos: [
        "Congreso con público externo → «Precio fijo» de $6.000.",
        "Jornada para estudiantes y docentes → «Tarifa de cada comprador»: cada uno paga su tarifa habitual.",
      ],
    },
  },
  {
    id: "cobro",
    tipo: "paso",
    numero: 4,
    icono: "point_of_sale",
    titulo: "Cobro e impresión",
    resumen: "Medios de pago e impresora.",
    etiqueta: "Obligatorio",
    detalle: {
      campos: [
        "Medios de pago habilitados en el punto de venta (Posnet es el recomendado).",
        "Impresora de comandas: la predeterminada o una de la lista.",
      ],
      restricciones: [
        "Hay que dejar al menos un medio de pago marcado.",
        "La impresora elegida tiene que existir y estar habilitada. Si la guardada ya no está disponible, hay que elegir otra.",
      ],
      aTenerEnCuenta: [
        "Con todos los medios marcados, los que se habiliten en el futuro quedan disponibles sin editar el evento.",
        "El vendedor confirma cada cobro antes de cerrar la venta (en transferencias, que el dinero esté acreditado).",
        "La impresora recibe los productos que no son del menú universitario.",
      ],
      ejemplos: [
        "Evento donde solo se cobra con tarjeta → desmarque «Transferencia» y deje «Posnet».",
      ],
    },
  },
  {
    id: "facturacion", tipo: "paso", numero: 5, icono: "receipt_long", titulo: "Facturación",
    resumen: "Descuentos por aplicación y tipo de usuario.",
    detalle: {
      campos: ["Porcentaje general opcional; cada aplicación decide si usarlo.", "Sin general activo: descuento propio del grupo, por tipo o cero.", "Limpiar valores en un grupo borra sólo sus porcentajes; el botón general borra todos."],
      restricciones: ["Porcentajes entre 0 y 100, con hasta dos decimales. Cero es explícito.", "Convenio admite Alumno, Docente y Personal de apoyo."],
      aTenerEnCuenta: ["Toda factura POS es B. Comunidad admitida: exenta; Particular: IVA 21 % incluido.", "Voucher MONTO se aplica después del evento; PRODUCTO/CATEGORÍA excluye del evento únicamente unidades cubiertas."]
    }
  },
  {
    id: "autogestion",
    tipo: "paso",
    numero: 6,
    icono: "phonelink_lock",
    titulo: "App de autogestión",
    resumen: "Qué se bloquea durante el evento.",
    etiqueta: "Opcional",
    detalle: {
      campos: [
        "Bloquear pedidos de menú (la cafetería sigue disponible).",
        "Bloquear pedidos de cafetería (el menú sigue disponible).",
        "Suspender toda la app (muestra solo un aviso de suspensión).",
        "Mensaje para los usuarios, cuando hay algún bloqueo.",
      ],
      restricciones: [
        "«Suspender toda la app» incluye los otros dos bloqueos: quedan marcados y no se pueden cambiar.",
        "El mensaje admite hasta 300 caracteres.",
      ],
      aTenerEnCuenta: [
        "Aplica a la app Sistema Comedor y al tótem solo mientras el evento está vigente.",
        "El punto de venta del evento y el panel administrativo nunca se bloquean.",
        "Si no marca nada, la app funciona normalmente. Si deja el mensaje vacío, se usa el texto predeterminado.",
      ],
      ejemplos: [
        "El comedor se usa para el evento → «Bloquear pedidos de menú»; la cafetería sigue tomando pedidos.",
        "Mensaje predeterminado: «Los pedidos de Menú no están disponibles durante este evento.»",
      ],
    },
  },
  {
    id: "revision",
    tipo: "paso",
    numero: 7,
    icono: "fact_check",
    titulo: "Revisar y guardar",
    resumen: "Resumen de todo lo cargado y controles automáticos.",
    etiqueta: "Obligatorio",
    detalle: {
      campos: [
        "Resumen de cada paso con un enlace «Cambiar» para volver a él.",
        "Lista de vouchers físicos vigentes en las fechas del evento.",
      ],
      restricciones: [
        "Si el horario se superpone con otro evento activo, se muestra un error y el botón «Crear evento» queda deshabilitado.",
      ],
      aTenerEnCuenta: [
        "Los avisos amarillos no impiden guardar: sin productos ni menú, falta la categoría «Menú universitario», no hay turnos activos o hay días sin platos en el cronograma.",
        "Un voucher físico se marca con advertencia si su grupo es de otra sede, no está activo o su producto/categoría no está en el catálogo del evento.",
        "Si los controles automáticos no pueden ejecutarse, se avisa y se puede guardar igual.",
      ],
      ejemplos: [
        "«Se superpone con «Feria de ciencias» (23/09/2026 10:00 a 23/09/2026 12:00). Cambie el horario o desactive uno de los dos.»",
        "«Sin platos en el cronograma para 24/09.» → cargue los platos en Menú universitario.",
      ],
    },
  },
  {
    id: "superposicion",
    tipo: "decision",
    icono: "help",
    titulo: "¿Se superpone con otro evento activo?",
    resumen: "El sistema lo controla al revisar y otra vez al guardar.",
    si: { texto: "Corrija el horario o desactive uno de los dos eventos", destino: "datos" },
    no: { texto: "Presione «Crear evento»" },
    detalle: {
      restricciones: [
        "La regla es global para la sede: no importa quién cargó el otro evento.",
        "Aunque el diagnóstico no lo haya detectado, si otro usuario guardó un evento en esa franja mientras tanto, el guardado se rechaza.",
      ],
      aTenerEnCuenta: [
        "Para resolverlo puede mover el inicio o el fin (paso 1) o dejar uno de los dos eventos desactivado.",
      ],
    },
  },
  {
    id: "fin",
    tipo: "fin",
    icono: "check",
    titulo: "Evento creado",
    resumen: "Aparece en el listado con su estado.",
    detalle: {
      campos: [
        "Estados: Programado (todavía no empezó), En curso, Finalizado o Inactivo (desactivado).",
        "Menú ⋮ del listado: Editar (o Ver, si ya finalizó), Duplicar o Eliminar.",
        "Clic en el estado del listado: pasar el evento a Activo o Inactivo sin abrir el asistente.",
        "En un evento finalizado, «Extender cierre» (en Ver) permite elegir un nuevo fin y volver a vender.",
      ],
      restricciones: [
        "Solo se pueden eliminar eventos sin ventas. Si tiene ventas, desactívelo desde el estado del listado o desde Editar.",
        "Un evento finalizado se abre solo para consulta; para repetirlo, duplíquelo.",
        "Un evento finalizado no cambia de estado: primero hay que extender su cierre.",
        "El nuevo cierre tiene que ser posterior a ahora y no tiene límite de duración; no puede superponerse con otro evento activo desde ahora hasta el nuevo fin.",
        "La superposición se controla desde ahora: un evento que ya terminó dentro del período no impide editar ni reactivar uno en curso.",
        "Si el evento extendido está inactivo, se pregunta si se activa o se mantiene inactivo.",
        "En curso o con ventas: el inicio queda fijo y los productos vendidos no se quitan (se pausan); precios, medios, impresora y bloqueos sí se cambian.",
        "Si otro usuario guardó el evento mientras usted lo editaba, el guardado se rechaza y se ofrece recargarlo.",
      ],
      aTenerEnCuenta: [
        "Al editar, las ventas ya registradas conservan sus precios.",
        "Desactivar un evento en curso pide confirmación: el punto de venta deja de vender en el momento y la app de autogestión vuelve a funcionar normalmente. Se puede reactivar mientras no termine.",
        "Al activar se controla que no se superponga con otro evento activo.",
        "Si los precios cambian mientras un vendedor arma una venta, el punto de venta avisa y pide confirmar el nuevo total.",
        "Durante la vigencia, el evento queda disponible en «Venta evento» para los vendedores.",
      ],
      ejemplos: [
        "Para la edición del año siguiente: «Duplicar», elegir las nuevas fechas y revisar precios.",
      ],
    },
  },
];
